import { apiClient } from './apiClient';
import { getNearbyHexagons as getNearbyHexagonsApi } from './hexagons';

export interface BasicInfo {
  fullName: string;
  businessName: string;
  mobileNumber: string;
  whatsappNumber?: string;
  employeeCode?: string;
  gstNumber?: string;
}

export interface ServiceDetail {
  category: string;
  subcategories: string[];
  serviceChargeCategory: 'lower' | 'same' | 'higher';
}

export interface ServiceDetails {
  services: ServiceDetail;
}

export interface CoverageArea {
  selectedPlan: {
    planId?: string;
    planName: string;
    hexCount: number | null;
    pricePerYear: number;
    sellingPrice?: number;
    isAllKerala?: boolean;
    isTrial?: string;
    trialDays?: number;
    maxUsersAllowed?: number;
  } | null;
  selectedHexagons: string[];
}

// Branch Details Types
export interface BranchLocation {
  type: 'Point';
  coordinates: [number, number];
  address?: string;
  landmark?: string;
  city?: string;
  district?: string;
  state?: string;
  pincode?: string;
}

export interface BranchOperatingHours {
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  open: string;
  close: string;
  isOpen: boolean;
}

export interface ContactPerson {
  name?: string;
  designation?: string;
  phone?: string;
  email?: string;
}

export interface BranchDetails {
  branchName: string;
  branchType: 'main' | 'franchise' | 'outlet' | 'service_center';
  contactPerson?: ContactPerson;
  phoneNumbers?: string[];
  emailAddresses?: string[];
  location: BranchLocation;
  operatingHours?: BranchOperatingHours[];
  nearbyHexagons?: string[];
}

export interface AdditionalInfo {
  additionalWorkQuantity?: number;
  incomeSharingPercentage: number;
}

export interface ListingDetails {
  listingAmount: number;
  selectedCategory?: string;
  selectedAreas?: string[];
  totalServiceCharge?: number;
}

export interface PaymentDetails {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
  amount: number;
  currency?: string;
}

export interface PartnerFormData {
  basicInfo: BasicInfo;
  serviceDetails: ServiceDetails;
  coverageAreas: CoverageArea;
  branchDetails: BranchDetails;
  additionalInfo: AdditionalInfo;
  listingDetails: ListingDetails;
  payment: PaymentDetails;
  documents?: string[];
}

export interface RazorpayOrderResponse {
  orderId: string;
  amount: number;
  currency: string;
  keyId: string;
}

export interface HexConflict {
  h3Index: string;
  subcategory: string;
}

export interface HexConflictCheckResponse {
  conflicts: HexConflict[];
  unavailableHexagons: string[];
}

export interface NearbyHexagon {
  h3Index: string;
  latitude: number;
  longitude: number;
  distance?: number;
  isAvailable?: boolean;
}

// Plan type matching the actual API response from your backend
export interface Plan {
  id: string;
  _id?: string;
  name: string;
  displayName: string;
  description: string;
  isTrial: string;
  trialDays: number;
  features: {
    hexCount: number;
    coverageAreaKm2: number;
    branchLimit: number;
    serviceCategories?: number;
    subcategoriesPerCategory?: number;
    isAllKeralaAllowed?: boolean;
  };
  pricing: {
    originalPrice: number;
    sellingPrice: number;
    payInitially?: number;
    payAfterSales?: number;
    executiveIncentivePercentage?: number;
    executiveIncentiveAmount?: number;
    discountedPrice: number | null;
    finalPrice: number;
    gstPercentage: number;
    costPerHex?: number;
  };
  offer: {
    type: string;
    value: number;
    description: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
  } | null;
  isPopular: boolean;
  sortOrder: number;
  availability: {
    totalSlots: number;
    usedSlots: number;
    availableSlots: number;
    isAvailable: boolean;
  } | null;
  basePrice?: number;
  costPerHex?: number;
  currentOffer?: any;
  type?: string;
  limits?: any;
  usersCount?: number;
}

/**
 * Get all public plans from backend
 */
export async function getPublicPlans(): Promise<Plan[]> {
  const response = await apiClient.get('/plans');

  if (response.success && response.data) {
    return response.data as Plan[];
  }

  throw new Error(response.message || 'Failed to fetch plans');
}

/**
 * Get trial plans only
 */
export async function getTrialPlans(): Promise<Plan[]> {
  const response = await apiClient.get('/plans/trial');

  if (response.success && response.data) {
    return response.data as Plan[];
  }

  throw new Error(response.message || 'Failed to fetch trial plans');
}

/**
 * Get regular (non-trial) plans only
 */
export async function getRegularPlans(): Promise<Plan[]> {
  const response = await apiClient.get('/plans/regular');

  if (response.success && response.data) {
    return response.data as Plan[];
  }

  throw new Error(response.message || 'Failed to fetch regular plans');
}

/**
 * Create Razorpay payment order
 */
export async function createPaymentOrder(
  amount: number,
  currency: string = 'INR',
  metadata?: Record<string, any>
): Promise<RazorpayOrderResponse> {
  const response = await apiClient.post<RazorpayOrderResponse>(
    '/payment/create-order',
    {
      amount,
      currency,
      metadata: {
        type: 'partner_registration',
        notes: {
          description: 'Partner Registration Payment',
          ...metadata,
        },
      },
    }
  );

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to create payment order');
}

/**
 * Verify Razorpay payment
 */
export async function verifyPayment(
  orderId: string,
  paymentId: string,
  signature: string
): Promise<void> {
  const response = await apiClient.post('/payment/verify', {
    razorpay_order_id: orderId,
    razorpay_payment_id: paymentId,
    razorpay_signature: signature,
  });

  if (!response.success) {
    throw new Error(response.message || 'Payment verification failed');
  }
}

/**
 * Upload documents for partner form
 */
export async function uploadDocuments(files: File[]): Promise<string[]> {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('documents', file);
  });

  const response = await apiClient.upload<{ documents: string[] }>(
    '/partner-form/upload-documents',
    formData
  );

  if (response.success && response.data?.documents) {
    return response.data.documents;
  }

  throw new Error(response.message || 'Failed to upload documents');
}

/**
 * Submit complete partner form
 */
export async function submitPartnerForm(data: PartnerFormData): Promise<any> {
  const response = await apiClient.post('/partner-form', data);

  if (response.success && response.data) {
    return response.data;
  }

  const errorMessage =
    response.errors && response.errors.length > 0
      ? `${response.message || 'Validation error'}: ${response.errors.join(', ')}`
      : response.message || 'Failed to submit partner form';

  const error: any = new Error(errorMessage);
  error.errors = response.errors;
  throw error;
}

/**
 * Get partner form by ID (for confirmation screen)
 */
export async function getPartnerFormById(id: string): Promise<any> {
  const response = await apiClient.get(`/partner-form/${id}`);

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to fetch partner form');
}

/**
 * Check for hexagon conflicts (subcategory + hexagon claimed)
 */
export async function checkHexConflicts(
  subcategories: string[],
  hexagons: string[],
  category?: string,
  excludeFormId?: string
): Promise<HexConflictCheckResponse> {
  const response = await apiClient.post<HexConflictCheckResponse>(
    '/partner-form/check-hex-conflicts',
    {
      subcategories,
      hexagons,
      category,
      excludeFormId,
    }
  );

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to check hexagon conflicts');
}

/**
 * Get nearby hexagons based on latitude and longitude
 */
export async function getNearbyHexagons(
  latitude: number,
  longitude: number,
  radiusKm: number = 25,
  subcategories?: string[],
  category?: string,
  excludeFormId?: string,
  excludePartnerId?: string
): Promise<NearbyHexagon[]> {
  return getNearbyHexagonsApi(latitude, longitude, radiusKm, subcategories, category, excludeFormId, excludePartnerId);
}

/**
 * Get hexagon by H3 index
 */
export async function getHexagonByIndex(h3Index: string): Promise<any> {
  const response = await apiClient.get(`/hexagons/${h3Index}`);

  if (response.success && response.data) {
    return response.data;
  }

  throw new Error(response.message || 'Failed to fetch hexagon details');
}

/**
 * Get all hexagons (for admin/map display)
 */
export async function getAllHexagons(subcategories?: string[]): Promise<any[]> {
  const params: Record<string, any> = {};
  if (subcategories && subcategories.length > 0) {
    params.subcategories = subcategories.join(',');
  }

  const response = await apiClient.get('/hexagons', params);

  if (response.success && response.data) {
    return response.data as any[];
  }

  return [];
}

export interface PhoneCheckResult {
  isRegistered: boolean;
  source?: "partner" | "application" | null;
  businessName?: string;
  message?: string;
  form?: any;
  partner?: any;
  isDraft?: boolean;
  canResume?: boolean;
}

/**
 * Check if a phone number is already registered in partner collection or applications
 */
export async function checkExistingFormByPhone(phone: string): Promise<PhoneCheckResult> {
  const response = await apiClient.get<any>(`/partner-form/check-phone/${phone}`);

  if (response.success) {
    const data = response.data;
    
    // When no form/partner found
    if (!data) {
      return {
        isRegistered: false,
        message: "Mobile number is available",
      };
    }

    // New backend format: { isRegistered: boolean, ... }
    if (typeof data.isRegistered === "boolean") {
      return data as PhoneCheckResult;
    }

    // Older backend format: { form: { ... }, isDraft: false, canResume: false, ... }
    const hasExistingForm = Boolean(data.form);
    const hasExistingPartner = Boolean(data.partner);
    const isRegistered = hasExistingForm || hasExistingPartner;

    const bName =
      data.businessName ||
      data.form?.basicInfo?.businessName ||
      data.partner?.businessDetails?.businessName ||
      "";

    return {
      isRegistered,
      source: hasExistingPartner ? "partner" : hasExistingForm ? "application" : null,
      businessName: bName,
      message: isRegistered
        ? bName
          ? `This mobile number is already registered for "${bName}". Please change the number to proceed.`
          : "This mobile number is already registered. Please change the number to proceed."
        : "Mobile number is available",
      form: data.form || null,
      partner: data.partner || null,
      isDraft: data.isDraft,
      canResume: data.canResume,
    };
  }

  throw new Error(response.message || 'Failed to check mobile number registration');
}